import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppContext, useAsyncData } from '@/hooks';
import { api } from '@/api/client';
import type { Campus } from '@/types';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { FormField } from '@/components/ui/FormField';
import { useToast } from '@/components/ui/Toast';
import { Icon } from '@/components/icons';
import styles from './AdminCampusesPage.module.css';

interface CampusForm {
  name: string;
  code: string;
  contactEmail: string;
  contactPhone: string;
  address: string;
}

const EMPTY_FORM: CampusForm = { name: '', code: '', contactEmail: '', contactPhone: '', address: '' };

export function AdminCampusesPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const { activeContext } = useAppContext();
  const institutionId = activeContext?.institution?.id ?? '';

  const { state, reload, replace } = useAsyncData<Campus[]>(
    `admin-campuses-${institutionId}`,
    () => api.admin.campuses.list(institutionId),
    !!institutionId,
  );

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{ open: boolean; campus: Campus | null; action: 'close' | 'suspend' | 'activate' }>({ open: false, campus: null, action: 'close' });
  const [form, setForm] = useState<CampusForm>(EMPTY_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [success, setSuccess] = useState('');

  const campuses = state.status === 'ready' ? state.data : [];

  const showSuccess = (msg: string) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleAdd = async () => {
    if (!institutionId) return;
    setSubmitting(true);
    try {
      const created = await api.admin.campuses.create(institutionId, form);
      if (campuses) replace([...campuses, created]);
      setAddModalOpen(false);
      setForm(EMPTY_FORM);
      showSuccess(t('admin.campuses.created'));
    } catch {
      toast.error(t('common.actionFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async () => {
    if (!institutionId || !editingId) return;
    setSubmitting(true);
    try {
      const updated = await api.admin.campuses.update(institutionId, editingId, form);
      if (campuses) replace(campuses.map((c) => (c.id === editingId ? updated : c)));
      setEditModalOpen(false);
      setEditingId(null);
      setForm(EMPTY_FORM);
      showSuccess(t('admin.campuses.saved'));
    } catch {
      toast.error(t('common.actionFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmAction = useCallback(async () => {
    if (!institutionId || !confirmModal.campus) return;
    const { campus, action } = confirmModal;
    setActionLoading(true);
    try {
      if (action === 'close') {
        await api.admin.campuses.close(institutionId, campus.id);
      } else if (action === 'suspend') {
        await api.admin.campuses.suspend(institutionId, campus.id);
      } else if (action === 'activate') {
        await api.admin.campuses.activate(institutionId, campus.id);
      }
      const updated: Campus = action === 'activate'
        ? { ...campus, status: 'ACTIVE' }
        : action === 'suspend'
          ? { ...campus, status: 'SUSPENDED' }
          : { ...campus, status: 'CLOSED' };
      if (campuses) replace(campuses.map((c) => (c.id === campus.id ? updated : c)));
      setConfirmModal({ open: false, campus: null, action: 'close' });
    } catch {
      toast.error(t('common.actionFailed'));
    } finally {
      setActionLoading(false);
    }
  }, [institutionId, confirmModal, campuses, replace]);

  const openEdit = (campus: Campus) => {
    setEditingId(campus.id);
    setForm({
      name: campus.name,
      code: campus.code,
      contactEmail: campus.contactEmail ?? '',
      contactPhone: campus.contactPhone ?? '',
      address: campus.address ?? '',
    });
    setEditModalOpen(true);
  };

  const statusClass = (status: string) => {
    switch (status) {
      case 'ACTIVE': return styles.statusActive;
      case 'DRAFT': return styles.statusDraft;
      case 'SUSPENDED': return styles.statusSuspended;
      case 'CLOSED': return styles.statusClosed;
      default: return styles.statusDraft;
    }
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerInfo}>
          <h1 className={styles.headerName}>{t('admin.campuses.title')}</h1>
          <p className={styles.headerSubtitle}>{t('admin.campuses.subtitle')}</p>
        </div>
        <Button onClick={() => { setForm(EMPTY_FORM); setAddModalOpen(true); }}>{t('admin.campuses.addCampus')}</Button>
      </header>

      {success && <div className={styles.successMessage}>{success}</div>}

      {state.status === 'loading' && (
        <div className={styles.loading}>{t('admin.campuses.loading')}</div>
      )}

      {state.status === 'error' && (
        <Card className={styles.emptyState}>
          <Icon name="error" size={48} />
          <h2 className={styles.emptyStateTitle}>{t('admin.campuses.title')}</h2>
          <p className={styles.emptyStateBody}>{t('common.error')}</p>
          <button className={styles.retryButton} onClick={reload}>{t('common.retry')}</button>
        </Card>
      )}

      {state.status === 'ready' && campuses.length === 0 && (
        <Card className={styles.emptyState}>
          <Icon name="campus" size={48} />
          <h2 className={styles.emptyStateTitle}>{t('admin.campuses.noCampuses')}</h2>
          <p className={styles.emptyStateBody}>{t('admin.campuses.createFirst')}</p>
          <Button onClick={() => { setForm(EMPTY_FORM); setAddModalOpen(true); }}>{t('admin.campuses.addCampus')}</Button>
        </Card>
      )}

      {state.status === 'ready' && campuses.length > 0 && (
        <Card>
          <div className="table-scroll">
            <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('admin.campuses.campusName')}</th>
                <th>{t('admin.campuses.campusCode')}</th>
                <th>{t('admin.campuses.status')}</th>
                <th>{t('admin.campuses.contactEmail')}</th>
                <th>{t('admin.campuses.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {campuses.map((campus) => (
                <tr key={campus.id}>
                  <td>{campus.name}</td>
                  <td>{campus.code}</td>
                  <td>
                    <span className={`${styles.statusBadge} ${statusClass(campus.status)}`}>
                      {t(`admin.status.${campus.status.toLowerCase()}`)}
                    </span>
                  </td>
                  <td>{campus.contactEmail ?? '—'}</td>
                  <td>
                    <div className={styles.actions}>
                      <Button size="sm" variant="secondary" onClick={() => openEdit(campus)}>{t('common.edit')}</Button>
                      {campus.status === 'SUSPENDED' && (
                        <Button size="sm" variant="secondary" onClick={() => setConfirmModal({ open: true, campus, action: 'activate' })}>{t('admin.campuses.activate')}</Button>
                      )}
                      {campus.status === 'ACTIVE' && (
                        <Button size="sm" variant="secondary" onClick={() => setConfirmModal({ open: true, campus, action: 'suspend' })}>{t('admin.campuses.suspend')}</Button>
                      )}
                      {(campus.status === 'DRAFT' || campus.status === 'ACTIVE' || campus.status === 'SUSPENDED') && (
                        <Button size="sm" variant="danger" onClick={() => setConfirmModal({ open: true, campus, action: 'close' })}>{t('admin.status.closed')}</Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            </table>
          </div>
        </Card>
      )}

      <Modal open={addModalOpen} onClose={() => setAddModalOpen(false)} title={t('admin.campuses.addDialogTitle')} closeOnBackdropClick={false}>
        <div className={styles.dialogForm} onKeyDown={(e) => { if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') { e.preventDefault(); if (form.name && form.code && !submitting) handleAdd(); } }}>
          <FormField id="add-name" label={t('admin.campuses.campusName')} required>
            <input id="add-name" className={styles.formField} placeholder={t('admin.campuses.namePlaceholder')} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </FormField>
          <FormField id="add-code" label={t('admin.campuses.campusCode')} required>
            <input id="add-code" className={styles.formField} placeholder={t('admin.campuses.codePlaceholder')} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
          </FormField>
          <FormField id="add-email" label={t('admin.campuses.contactEmail')}>
            <input id="add-email" type="email" className={styles.formField} placeholder={t('admin.campuses.emailPlaceholder')} value={form.contactEmail} onChange={(e) => setForm({ ...form, contactEmail: e.target.value })} />
          </FormField>
          <FormField id="add-phone" label={t('admin.campuses.contactPhone')}>
            <input id="add-phone" type="tel" className={styles.formField} placeholder={t('admin.campuses.phonePlaceholder')} value={form.contactPhone} onChange={(e) => setForm({ ...form, contactPhone: e.target.value })} />
          </FormField>
          <FormField id="add-address" label={t('admin.campuses.address')}>
            <input id="add-address" className={styles.formField} placeholder={t('admin.campuses.addressPlaceholder')} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          </FormField>
          <div className={styles.dialogActions}>
            <Button variant="secondary" onClick={() => setAddModalOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleAdd} loading={submitting} disabled={!form.name || !form.code || submitting}>{t('common.save')}</Button>
          </div>
        </div>
      </Modal>

      <Modal open={editModalOpen} onClose={() => setEditModalOpen(false)} title={t('admin.campuses.editDialogTitle')} closeOnBackdropClick={false}>
        <div className={styles.dialogForm} onKeyDown={(e) => { if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') { e.preventDefault(); if (form.name && form.code && !submitting) handleEdit(); } }}>
          <FormField id="edit-name" label={t('admin.campuses.campusName')} required>
            <input id="edit-name" className={styles.formField} placeholder={t('admin.campuses.namePlaceholder')} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </FormField>
          <FormField id="edit-code" label={t('admin.campuses.campusCode')} required>
            <input id="edit-code" className={styles.formField} placeholder={t('admin.campuses.codePlaceholder')} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
          </FormField>
          <FormField id="edit-email" label={t('admin.campuses.contactEmail')}>
            <input id="edit-email" type="email" className={styles.formField} placeholder={t('admin.campuses.emailPlaceholder')} value={form.contactEmail} onChange={(e) => setForm({ ...form, contactEmail: e.target.value })} />
          </FormField>
          <FormField id="edit-phone" label={t('admin.campuses.contactPhone')}>
            <input id="edit-phone" type="tel" className={styles.formField} placeholder={t('admin.campuses.phonePlaceholder')} value={form.contactPhone} onChange={(e) => setForm({ ...form, contactPhone: e.target.value })} />
          </FormField>
          <FormField id="edit-address" label={t('admin.campuses.address')}>
            <input id="edit-address" className={styles.formField} placeholder={t('admin.campuses.addressPlaceholder')} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          </FormField>
          <div className={styles.dialogActions}>
            <Button variant="secondary" onClick={() => setEditModalOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleEdit} loading={submitting} disabled={!form.name || !form.code || submitting}>{t('common.save')}</Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={confirmModal.open}
        onClose={() => setConfirmModal({ open: false, campus: null, action: 'close' })}
        title={confirmModal.action === 'close' ? t('admin.campuses.confirmDelete') : confirmModal.action === 'suspend' ? t('admin.campuses.confirmSuspend') : t('admin.campuses.confirmActivate')}
      >
        <div className={styles.dialogActions}>
          <Button variant="secondary" onClick={() => setConfirmModal({ open: false, campus: null, action: 'close' })}>{t('common.cancel')}</Button>
          <Button variant="danger" onClick={handleConfirmAction} loading={actionLoading} disabled={actionLoading}>{t('common.confirm')}</Button>
        </div>
      </Modal>
    </div>
  );
}
